/**
 * Utility functions for handling sequence reordering in hierarchical program structures
 */

export interface SequenceItem {
  id: string;
  sequence?: number;
}

/**
 * Move an item up in the sequence (decrease sequence number)
 */
export const moveUp = <T extends SequenceItem>(
  item: T,
  allItems: T[]
): { item: T; swapItem: T | null } | null => {
  const sortedItems = [...allItems].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
  const currentIndex = sortedItems.findIndex((i) => i.id === item.id);

  if (currentIndex <= 0) {
    return null; // Already at the top
  }

  const currentItem = sortedItems[currentIndex];
  const previousItem = sortedItems[currentIndex - 1];
  return {
    item: { ...currentItem, sequence: previousItem.sequence },
    swapItem: { ...previousItem, sequence: currentItem.sequence },
  };
};

/**
 * Move an item down in the sequence (increase sequence number)
 */
export const moveDown = <T extends SequenceItem>(
  item: T,
  allItems: T[]
): { item: T; swapItem: T | null } | null => {
  const sortedItems = [...allItems].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
  const currentIndex = sortedItems.findIndex((i) => i.id === item.id);

  if (currentIndex === -1 || currentIndex >= sortedItems.length - 1) {
    return null; // Already at the bottom
  }

  const currentItem = sortedItems[currentIndex];
  const nextItem = sortedItems[currentIndex + 1];
  return {
    item: { ...currentItem, sequence: nextItem.sequence },
    swapItem: { ...nextItem, sequence: currentItem.sequence },
  };
};

/**
 * Check if an item can be moved up
 */
export const canMoveUp = <T extends SequenceItem>(
  item: T,
  allItems: T[]
): boolean => {
  const sortedItems = [...allItems].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
  const currentIndex = sortedItems.findIndex((i) => i.id === item.id);
  return currentIndex > 0;
};

/**
 * Check if an item can be moved down
 */
export const canMoveDown = <T extends SequenceItem>(
  item: T,
  allItems: T[]
): boolean => {
  const sortedItems = [...allItems].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
  const currentIndex = sortedItems.findIndex((i) => i.id === item.id);
  return currentIndex !== -1 && currentIndex < sortedItems.length - 1;
};

